import React, { type ComponentType, type ReactNode, type ReactElement } from 'react';
import Popup, {
	type PopupComponentProps,
	type PopupProps,
	type TriggerProps,
} from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import type { AdditionalEventAttributes } from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	AsyncLoadOptions,
	FormatOptionLabel,
	OnChangeWithData,
	OnChangeOperator,
	SelectValue,
	PickerComponents,
	AllowedOperators,
	OperatorOption,
	OnRestoreDefaults,
} from '../common/types.tsx';
import PaginatedAsyncSelectView from './async-select/index.tsx';
import formatOptionLabelDefault from './format-option-label/index.tsx';
import { FixedWidthPopupComponent } from './popup-container/index.tsx';

export type ComponentProps = PopupComponentProps;

type WithOperatorsSelectProps = {
	/**
	 * Show operator select if true
	 */
	showOperatorSelect: true;
	/**
	 * Callback called everytime operator is changed
	 */
	onChangeOperator: OnChangeOperator;
	/**
	 * List of operators to be use as options insde the operator select
	 */
	allowedOperators: AllowedOperators;
	/**
	 * Selected operator
	 */
	selectedOperator?: OperatorOption | undefined;
};

type WithoutOperatorsSelectProps = {
	showOperatorSelect?: false;
	onChangeOperator?: OnChangeOperator;
	allowedOperators?: AllowedOperators;
	selectedOperator?: OperatorOption | undefined;
};

type OperatorsSelectProps = WithOperatorsSelectProps | WithoutOperatorsSelectProps;

export type PickerProps = OperatorsSelectProps & {
	/**
	 * When true, dropdown is visible
	 * When false, dropdown is hidden
	 */
	isOpen: boolean;
	/**
	 * Unique id of picker
	 */
	id: string;
	/**
	 * Tooltip message when hover invalid option
	 */
	invalidLabel?: string;
	/**
	 * Array of selected values
	 */
	value: SelectValue;
	/**
	 * Function that is provided with option data and determines the look and feel of each option
	 * in the picker, for example `avatar` or `lozenge`. Providing this prop will override the
	 * default `formatOptionLabel` function that gets used - see `../format-option-label/index.tsx`
	 */
	formatOptionLabel?: FormatOptionLabel;
	/**
	 * Enable full list mode of picker
	 */
	enableFullList?: boolean;
	/**
	 * Operand display values that are not fetched in the loadOptions (eg: currentUser(), EMPTY)
	 */
	operandDisplayValues?: string[];
	/**
	 * When true, we don't add operandDisplayValues.length to totalCount
	 * When false, we add operandDisplayValues.length to totalCount
	 */
	showOperandDisplayValuesInFullList?: boolean;
	/**
	 * PopupComponent prop from https://atlaskit.atlassian.com/packages/design-system/popup
	 */
	PopupComponent?: ComponentType<PopupComponentProps>;
	/**
	 * Placement of popper, see https://popper.js.org/docs/v2/constructors/#options
	 * Placement of dropdown related to trigger component
	 */
	placement?: PopupProps['placement'];
	/**
	 * The z-index of the popup component
	 */
	zIndex?: number;
	/**
	 * Function to return the React node to display when there are no options
	 */

	CustomNoOptionsMessage?: (data: { inputValue: string }) => ReactNode;
	/**
	 * A subset of components that can be replaced in the underlying react-select component, see https://react-select.com/props#replacing-components.
	 */
	pickerComponents?: PickerComponents;
	/**
	 * List of placements to try. When no space is available on the preferred placement
	 */
	fallbackPlacements?: PopupProps['fallbackPlacements'];
	/**
	 * Trigger prop from https://atlaskit.atlassian.com/packages/design-system/popup
	 * Render trigger component (picker button)
	 * IMPORTANT: Node should be wrapped with fordwardRef (see src/packages/jql-builder/basic/src/common/ui/picker-trigger/main.js)
	 */
	onTrigger: (arg1: TriggerProps) => ReactElement;
	/**
	 * Callback called when "Clear selection" button is clicked
	 */
	onClearValues?: () => void;
	/**
	 * onClose prop from https://atlaskit.atlassian.com/packages/design-system/popup
	 * trigger when clicking outside of popup or pressing esc key
	 */
	onClose: (
		event:
			| Event
			| KeyboardEvent
			| React.MouseEvent<HTMLElement>
			| React.KeyboardEvent<HTMLDivElement>,
	) => void;
	/**
	 * onChange callback from https://atlassian.design/components/select
	 * callback called every time an item is selected/unselected
	 */
	onChange: OnChangeWithData;
	/**
	 * Callback called to load more options
	 */
	onLoadOptions: AsyncLoadOptions;
	/**
	 * Callback called when "Restore defaults" button is clicked
	 */
	onRestoreDefaults?: OnRestoreDefaults;
	/**
	 * Determines if the picker is a multi select
	 */
	isMulti?: boolean;
	/**
	 * Placeholder text for select search input field - default value is `Search`
	 */
	searchInputPlaceholder?: string;
	/**
	 * shouldRenderToParent prop from https://atlaskit.atlassian.com/packages/design-system/popup
	 * Render picker popup either next to the trigger or in a portal. Defaults to false (rendering in a portal)
	 */
	shouldRenderToParent?: boolean;
	/**
	 * Additional attributes to be sent only in experience analytics events.
	 * Consumers should provide a stable object reference as a new experience event will be re-emitted if the reference changes
	 */
	experienceAnalyticsAttributes?: AdditionalEventAttributes;
	/**
	 * When true, we show a loading spinner
	 */
	isLoading?: boolean;
	/**
	 *
	 */
	CustomFooter?: ReactNode;
	/**
	 * Whether the column picker is displayed within the table
	 */
	isInBetweenColumnPickerEnabled?: boolean;
	/* Whether the picker should use infinite scroll.
	 */
	isInfiniteScrollEnabled?: boolean;
};

export const Picker = ({
	isOpen: isPickerOpen,
	value,
	onClose,
	onClearValues,
	onRestoreDefaults,
	id,
	invalidLabel,
	onChange,
	formatOptionLabel = formatOptionLabelDefault,
	onLoadOptions,
	enableFullList,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	PopupComponent,
	onTrigger,
	placement = 'bottom-start',
	zIndex,
	CustomNoOptionsMessage,
	pickerComponents,
	fallbackPlacements,
	showOperatorSelect = false,
	onChangeOperator,
	allowedOperators,
	selectedOperator,
	isMulti,
	searchInputPlaceholder,
	shouldRenderToParent = false,
	experienceAnalyticsAttributes,
	isLoading = false,
	CustomFooter,
	isInBetweenColumnPickerEnabled = false,
	isInfiniteScrollEnabled = false,
}: PickerProps) => (
	<Box
		role="presentation"
		onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Escape') {
				// avoid closing a modal on closing a picker with esc key
				isPickerOpen && event.stopPropagation();
				onClose && onClose(event);
			}
		}}
	>
		<Popup
			key={id}
			zIndex={zIndex}
			placement={placement}
			isOpen={isPickerOpen}
			onClose={onClose}
			popupComponent={PopupComponent ?? FixedWidthPopupComponent}
			trigger={onTrigger}
			autoFocus={false}
			fallbackPlacements={fallbackPlacements}
			testId={`jql-builder-basic-picker-${id}`}
			shouldRenderToParent={shouldRenderToParent}
			/* Our default popup component supports resizing to fit the viewport. */
			shouldFitViewport={PopupComponent === undefined}
			content={({ setInitialFocusRef }) => (
				<PaginatedAsyncSelectView
					id={id}
					invalidLabel={invalidLabel}
					enableFullList={enableFullList}
					operandDisplayValues={operandDisplayValues}
					showOperandDisplayValuesInFullList={showOperandDisplayValuesInFullList}
					loadOptions={onLoadOptions}
					onChange={onChange}
					value={value}
					formatOptionLabel={formatOptionLabel}
					onClear={onClearValues}
					onRestoreDefaults={onRestoreDefaults}
					NoOptionsMessage={CustomNoOptionsMessage}
					components={pickerComponents}
					showOperatorSelect={showOperatorSelect}
					onChangeOperator={onChangeOperator}
					allowedOperators={allowedOperators}
					selectedOperator={selectedOperator}
					isMulti={isMulti}
					placeholder={searchInputPlaceholder}
					setInitialFocusRef={setInitialFocusRef}
					experienceAnalyticsAttributes={experienceAnalyticsAttributes}
					isLoading={isLoading}
					isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
					CustomFooter={fg('jira_inline_field_config_gate') ? CustomFooter : undefined}
					isInfiniteScrollEnabled={isInfiniteScrollEnabled}
				/>
			)}
		/>
	</Box>
);

Picker.whyDidYouRender = {
	customName: 'Picker',
};
